import React, { useEffect, useRef, useState } from "react";
import formBaseStyles from "../text-input/styles.module.scss";
import styles from "./styles.module.scss";
import CaretDown from "../../../assets/svg/icons/common/CaretDown";
import SearchIcon from "../../../assets/svg/icons/common/search";
import TextInput from "../text-input";
import BlueTick from "../../../assets/svg/icons/common/blue-tick";
import ClearIcon from "../../../assets/svg/icons/common/CloseIcon";
import Button from "../../new-button";
import Spinner from "../../Loaders/Spinner";
import useDomainStore from "../../../stores/domain";
import { isScrolledNearBottom } from "../../../utils/is-scrolled-near-bottom";

const Option = ({ children, option, handleOptionSelect, blank, divider = false }) => {
  if (divider) {
    return (
      <div className={styles.dividerOption}>
        <span>{option?.label}</span>
      </div>
    );
  }

  return (
    <div
      tabIndex={0}
      className={`${styles.option} ${blank && styles.blank}`}
      onClick={() => handleOptionSelect(option)}
      onKeyUp={(e) => e.key === "Enter" && handleOptionSelect(option)}
    >
      {children}
    </div>
  );
};

const UserItem = ({ option, selected, isKurvDomain }) => {
  return (
    <div className={styles.userItem}>
      {selected && (
        <div className={styles.selected}>
          <BlueTick fill={isKurvDomain ? "#22A276" : "#4C70EF"} />
        </div>
      )}
      <div className={styles.avatar}>{option.label.charAt(0)}</div>
      <p className={styles.name}>{option.label}</p>
    </div>
  );
};

export const Select = ({
  id,
  name,
  label,
  placeholder,
  prefix,
  suffix,
  value,
  users = false,
  search = false,
  options,
  onChange,
  onBlur,
  error = "",
  errored = false,
  touched = false,
  dropdownHeader = "",
  placeholderAsBlankOption = false,
  secondary = false,
  customClass = "",
  disabled = false,
  async = false,
  onSearchChange = null,
  debounceDelay = 250,
  prevSelected = null,
  renderOption = null,
  hideClearButton = false,
  onLoadMore = null,
  hasMore = false,
  loadingMore = false,
}) => {
  const { isKurvDomain } = useDomainStore();
  const initialValue = useRef(value);
  const selectContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const prevSelectedValue = useRef(prevSelected || { value: value, label: "" });
  const timeoutRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const nonAsyncFilteredOptions = async
    ? []
    : options?.reduce(
        (acc, option) => {
          if (!searchTerm) {
            acc.items.push(option);
            return acc;
          }

          if (option?.divider) {
            acc.pendingDivider = option;
            return acc;
          }

          const matchesSearch = option?.label
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

          if (matchesSearch) {
            if (acc.pendingDivider) {
              acc.items.push(acc.pendingDivider);
              acc.pendingDivider = null;
            }
            acc.items.push(option);
          }

          return acc;
        },
        { items: [], pendingDivider: null },
      )?.items ?? [];

  const handleOptionSelect = (option, blank = false) => {
    if (disabled) return;
    if (option?.divider) return;
    setDropdownOpen(false);

    const optionValue = option?.value;
    const valueIsObject = typeof value === "object";

    const isSame =
      blank ||
      (valueIsObject && value?.value == optionValue) ||
      (!valueIsObject && value == optionValue);

    onChange(isSame ? "" : valueIsObject ? option : optionValue);

    // Reset async options if cleared
    if (async && onSearchChange) {
      onSearchChange("");
    }
  };

  const handleOnSearchInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (async && onSearchChange) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setResultsLoading(true);
        Promise.resolve(onSearchChange(term)).finally(() =>
          setResultsLoading(false),
        );
      }, debounceDelay);
    }
  };

  const handleDropdownScroll = (e) => {
    if (!async || !onLoadMore || !hasMore || loadingMore) return;
    if (isScrolledNearBottom(e.target)) {
      onLoadMore();
    }
  };

  const handleBlur = (e) => {
    if (
      selectContainerRef.current &&
      !selectContainerRef.current.contains(e.relatedTarget)
    ) {
      setDropdownOpen(false);
      if (onBlur) {
        onBlur({
          target: {
            name,
            value:
              (typeof initialValue.current === "object" && value?.value) ||
              (typeof initialValue.current === "string" && value) ||
              "",
          },
          persist: () => {},
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchTerm("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen && async && search) {
      const timeout = setTimeout(() => {
        if (searchInputRef?.current?.focus) {
          searchInputRef.current.focus();
        }
      }, 100);
      // I do not like this, but it works.
      // This probably has something to do with input is
      // not being rendered yet.

      return () => clearTimeout(timeout);
    }
  }, [dropdownOpen, async, search]);

  useEffect(() => {
    const option = options?.find((opt) => !opt?.divider && opt.value === value);
    if (option) {
      prevSelectedValue.current = { value: value, label: option.label };
    }
  }, [value, options]);

  const handleBaseInputClick = () => {
    if (disabled) return;
    setDropdownOpen((prev) => !prev);
  };

  const getSelectedValue = () => {
    if (!value) return placeholder;

    if (typeof value === "object" && value.label) {
      if (renderOption) {
        return renderOption(value, true);
      }
      return value.label;
    }

    const matched = options?.find((opt) => !opt?.divider && opt?.value === value);

    if (matched) {
      if (renderOption) {
        return renderOption(matched, true);
      }
      return matched?.label;
    }

    if (async) {
      if (value?.label) {
        if (renderOption) {
          return renderOption(value, true);
        }
        return value.label;
      }

      if (
        prevSelectedValue.current?.value &&
        prevSelectedValue.current?.label
      ) {
        if (renderOption) {
          return renderOption(prevSelectedValue.current, true);
        }
        return prevSelectedValue.current?.label;
      }
    }

    return placeholder;
  };

  const hasError = touched && errored && error;

  return (
    <div
      className={`${styles.selectContainer} ${secondary && styles.secondary} ${customClass} ${disabled ? styles.disabled : ""} ${hasError ? styles.errored : ""} ${isKurvDomain ? styles.kurv_theme : ""}`}
      ref={selectContainerRef}
      onBlur={handleBlur}
      id={id}
    >
      {label && (
        <label className={formBaseStyles.label} htmlFor={id}>
          {label}
        </label>
      )}

      {!hideClearButton && value && (
        <div className={`${styles.clearButton}`}>
          <Button
            type="button"
            icon={<ClearIcon />}
            variant="ghost"
            size="small"
            onClick={() =>
              handleOptionSelect(
                typeof initialValue.current === "object" ? null : "",
                true,
              )
            }
          />
        </div>
      )}

      <div
        className={`${formBaseStyles.input} ${styles.input}`}
        tabIndex={disabled ? -1 : 0}
        onClick={handleBaseInputClick}
        onKeyUp={(e) =>
          !disabled && e.key === "Enter" && setDropdownOpen((prev) => !prev)
        }
      >
        <p className={styles.inputSelection}>{getSelectedValue()}</p>

        <div className={`${styles.caret} ${dropdownOpen && styles.open}`}>
          <CaretDown />
        </div>
      </div>

      <div
        className={`${styles.dropdownContainer} ${dropdownOpen && styles.open}`}
      >
        {search && (
          <div className={styles.searchField}>
            <SearchIcon />
            <TextInput
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleOnSearchInputChange}
              className={`${formBaseStyles.input} ${styles.searchInput}`}
            />
            {resultsLoading && (
              <div className={styles.resultsLoadingSpinner}>
                <Spinner size={18} />
              </div>
            )}
          </div>
        )}

        <div className={`${styles.dropdown}`} onScroll={handleDropdownScroll}>
          {!options && <div className={styles.option}>No options</div>}

          {options && dropdownHeader && (
            <span className={styles.dropdownHeader}>{dropdownHeader}</span>
          )}

          {options && placeholderAsBlankOption && searchTerm.length === 0 && (
            <Option
              key={`blank-option-${label}`}
              option={{ value: "", label: placeholder }}
              handleOptionSelect={handleOptionSelect}
              blank
            >
              {placeholder}
            </Option>
          )}

          {options && searchTerm.length > 0 && (
            <>
              {(async && options.length === 0) ||
              (!async && nonAsyncFilteredOptions.length === 0) ? (
                <div className={styles.option}>No results found</div>
              ) : null}
            </>
          )}

          {options && (
            <>
              {(async
                ? options
                : nonAsyncFilteredOptions
              ).map((option, index) => (
                <Option
                  key={`${option.value}-${index}`}
                  option={option}
                  divider={option?.divider}
                  handleOptionSelect={handleOptionSelect}
                >
                  {option?.divider ? (
                    option.label
                  ) : users ? (
                    <UserItem
                      option={option}
                      selected={
                        value?.value == option.value || value == option.value
                      }
                      isKurvDomain={isKurvDomain}
                    />
                  ) : renderOption ? (
                    renderOption(
                      option,
                      value?.value == option.value || value == option.value,
                    )
                  ) : (
                    option.label
                  )}
                </Option>
              ))}
              {async && loadingMore && (
                <div className={styles.loadMoreSpinner}>
                  <Spinner size={18} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {hasError && <span className={formBaseStyles.error}>{error}</span>}
    </div>
  );
};

export default Select;
